// customFetcher.ts
export interface CustomFetcherOptions {
    endpoint: string;
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: unknown;
    signal?: AbortSignal;
    headers?: Record<string, string>;
}

const buildRequestOptions = (
    method: CustomFetcherOptions['method'],
    body: CustomFetcherOptions['body'],
    signal: CustomFetcherOptions['signal'],
    headers: Record<string, string>,
): RequestInit => {
    const combinedHeaders: HeadersInit = { ...headers };

    let payload: BodyInit | undefined;
    if (body instanceof FormData) {
        payload = body;
    } else if (body !== undefined) {
        combinedHeaders['Content-Type'] = 'application/json';
        payload = JSON.stringify(body);
    }

    return {
        method,
        headers: combinedHeaders,
        body: payload,
        signal,
        credentials: 'include',
    };
};

const performAuthenticatedRequest = async ({
    endpoint,
    method = 'GET',
    body,
    signal,
    headers = {},
}: CustomFetcherOptions): Promise<Response> => {
    const apiEndpoint = `/api${endpoint}`;

    let response = await fetch(apiEndpoint, buildRequestOptions(method, body, signal, headers));

    if (response.status === 401) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
            response = await fetch(apiEndpoint, buildRequestOptions(method, body, signal, headers));
        }
    }

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response;
};

async function refreshAccessToken(): Promise<boolean> {
    try {
        const response = await fetch('/api/token/refresh/cookie/', {
            method: 'POST',
            credentials: 'include',
        });
        if (!response.ok) {
            localStorage.removeItem('groups');
            localStorage.removeItem('is_superadmin');
            localStorage.removeItem('user_id');
            window.dispatchEvent(new Event('auth:session-expired'));
            return false;
        }
        return true;
    } catch {
        return false;
    }
}

export const customFetcher = async <T,>({
    endpoint,
    method = 'GET',
    body,
    signal,
    headers = {},
}: CustomFetcherOptions): Promise<T> => {
    const response = await performAuthenticatedRequest({
        endpoint,
        method,
        body,
        signal,
        headers,
    });

    if (response.status === 204) {
        return {} as T;
    }

    return response.json() as Promise<T>;
};

export const customFetcherRaw = async (options: CustomFetcherOptions): Promise<Response> =>
    performAuthenticatedRequest(options);
